import axios from '../../config/axios';
import { API_ENDPOINTS } from '../../config/api';
import { generateContent } from '../../services/AIService';
import { TEMPLATE_INTAKE_FIELD_META } from '../../data/templateIntakeCheckboxFieldMap';
import { templateLanguageUpdatedBase } from '../../data/templateLanguageUpdatedBase';
import { buildCptTemplateLlmPromptSections } from '../../utils/cptTemplateConventions';
import { cptHtmlToPlainText, coerceCptTemplateHtml, shouldUseCptColoredOutput } from '../../utils/cptTemplateHtml';
import { normalizeTemplateLanguagePlainText } from '../../utils/normalizeTemplateLanguagePlainText';
import { stripLeadingDuplicateHeading } from '../../utils/stripLeadingDuplicateHeading';
import { applyGrammarGuardsToHtml } from '../../utils/grammarGuards';
import { buildUsdmCatalogueGlobalContext } from './usdmCatalogueContext';
import { getUsdmProtocolSections } from '../constants/usdmProtocolSectionData';

/**
 * USDM-sourced counterpart to ClinicalIntakeForm.jsx's per-field "Use
 * Template" checkboxes (runIntakeTemplateCheckboxLlm) — same
 * templateIntakeCheckboxFieldMap.js field list, same CPT base template +
 * LLM tailoring + ADK review pipeline, reusing the exact same shared
 * utilities, but tailored against the uploaded USDM catalogue's facts
 * instead of a clinical intake record.
 */

/**
 * These 4 fields require a Clinical Intake decision-tree answer with no USDM
 * equivalent at all (Patient Library CBP/NCBP pathway; Section 7.1.2/7.1.3
 * decision panels) — ClinicalIntakeForm.jsx assembles them deterministically
 * from specific intake form fields (patientLibrarySection54.js,
 * temporaryDiscontinuationSection712.js, rechallengeSection713.js), never
 * via an LLM. A USDM catalogue has no matching data those assemblers could
 * read, so these are reported unsupported rather than faked with a
 * best-guess LLM call the intake flow deliberately never makes either.
 *
 * Exception: for definitions_childbearing_potential/contraception_requirements
 * specifically, UsdmTemplateTab.jsx's "Auto-select from USDM" button (see
 * patientLibrarySection54UsdmInference.js) DOES use an LLM — but only to pick
 * which decision-tree ANSWER applies, from this study's USDM facts. The two
 * fields listed below still stay exactly as unsupported for direct
 * generation: their actual protocol TEXT is always produced by
 * assemblePatientLibrarySection54() verbatim from the Patient Library, never
 * authored or rewritten by an LLM.
 */
export const UNSUPPORTED_USDM_TEMPLATE_FIELDS = {
  definitions_childbearing_potential:
    'No USDM equivalent — never LLM-generated. Answer the Patient Library decision tree in the "Patient Library — Sections 5.4.1 & 5.4.2" panel above instead.',
  contraception_requirements:
    'No USDM equivalent — never LLM-generated. Answer the Patient Library decision tree in the "Patient Library — Sections 5.4.1 & 5.4.2" panel above instead.',
  rechallenge:
    'Requires the Section 7.1.3 decision panel from the Protocol Intake Form — no USDM equivalent.',
  temporary_discontinuation_trial_intervention:
    'Requires the Section 7.1.2 decision panel from the Protocol Intake Form — no USDM equivalent.',
};

// Mirrors ClinicalIntakeForm.jsx's own SIBLING_HEADING_STRIP_RULES (a stray-
// heading cleanup backstop for one specific field) — copied rather than
// imported since it's defined locally there, not exported.
const SIBLING_HEADING_STRIP_RULES = {
  ae_reporting_timing_procedures: /^[ \t]*9\.2\.\d+\b[^:\n]*$\n?/gm,
};

function stripSiblingHeadingLines(text, fieldName) {
  const re = SIBLING_HEADING_STRIP_RULES[fieldName];
  if (!re || !text) return text || '';
  return String(text).replace(re, '').replace(/\n{3,}/g, '\n\n').trim();
}

/** Leading top-level section number from a title, e.g. "9.1.1 Definition..." -> "9". */
function leadingSectionNumber(title) {
  const m = String(title || '').trim().match(/^(\d+)/);
  return m ? m[1] : null;
}

/**
 * Which of getUsdmProtocolSections()'s top-level sections a given template
 * field's generated text belongs under, by matching leading section numbers
 * (e.g. "9.1.1 Definition of Adverse event" -> "9. ADVERSE EVENTS...").
 * Template Language and USDM's section list both use the same ICH M11
 * numbering scheme, so this holds without a hand-maintained mapping.
 * Returns null if the field or a matching top-level section can't be found.
 */
export function resolveUsdmSectionForField(fieldName) {
  const meta = TEMPLATE_INTAKE_FIELD_META[fieldName];
  if (!meta) return null;
  const num = leadingSectionNumber(meta.sectionLabel || meta.baseTitle);
  if (!num) return null;
  return getUsdmProtocolSections().find((s) => leadingSectionNumber(s.title) === num) || null;
}

/**
 * @param {string} fieldName - a key from TEMPLATE_INTAKE_FIELD_META
 * @param {object} usdmFacts - normalizeUsdmCatalogue()'s output
 * @returns {Promise<{ok:true, finalText:string} | {ok:false, unsupported?:boolean, message:string}>}
 */
export async function generateUsdmTemplateField(fieldName, usdmFacts) {
  if (UNSUPPORTED_USDM_TEMPLATE_FIELDS[fieldName]) {
    return { ok: false, unsupported: true, message: UNSUPPORTED_USDM_TEMPLATE_FIELDS[fieldName] };
  }
  const meta = TEMPLATE_INTAKE_FIELD_META[fieldName];
  if (!meta) {
    return { ok: false, message: `Unknown template field: ${fieldName}` };
  }

  const usdmContext = buildUsdmCatalogueGlobalContext(usdmFacts);
  const standardBlock = (meta.baseTitle ? templateLanguageUpdatedBase[meta.baseTitle] : '') || '';
  if (!standardBlock && !meta.allowEmptyBase) {
    return { ok: false, message: 'No standard template text available for this field.' };
  }

  const useCptColor = shouldUseCptColoredOutput(fieldName);
  const cptSections = buildCptTemplateLlmPromptSections({
    sectionLabel: meta.sectionLabel,
    fieldName,
    standardBlock,
    focusNote: meta.focusNote || '',
    colorCodedHtml: useCptColor,
    sourceLabel: 'the uploaded USDM catalogue',
  });

  const prompt = `ROLE: You are an expert medical writer drafting a single Clinical Trial Protocol subsection using the CPT Core Basic Word Edition (BWE) template rules.

Produce FINAL protocol text for this section ONLY: "${meta.sectionLabel}"

CRITICAL: Do NOT copy, reuse, or paraphrase language from uploaded protocol documents or any previously saved template-language fields. Generate from the CPT standard template block below and tailor using factual study data only (design, population, treatments, endpoints, regulatory region, etc.), sourced from the uploaded USDM catalogue.

${cptSections}

=== Full standard template block (reference — apply colour-coding rules above) ===
${standardBlock ||
    '(No base block was supplied. Write concise, regulator-aligned protocol language appropriate for this section title, consistent with ICH GCP and common practice.)'}

=== End reference block ===
`;

  let text;
  try {
    text = await generateContent(prompt, usdmContext || '', '', 'protocol', meta.sectionLabel, null);
  } catch (err) {
    return { ok: false, message: err.message || 'Failed to generate template text.' };
  }

  const textNoSiblingHeadings = stripSiblingHeadingLines(text, fieldName);
  const cleanedRaw = useCptColor
    ? coerceCptTemplateHtml((textNoSiblingHeadings || '').trim())
    : normalizeTemplateLanguagePlainText((textNoSiblingHeadings || '').trim());
  const cleaned = stripLeadingDuplicateHeading(cleanedRaw, meta.sectionLabel);
  if (!cleaned) {
    return { ok: false, message: 'Empty response from model.' };
  }

  let finalText = cleaned;
  try {
    const reviewTitle = (meta.baseTitle || meta.sectionLabel || '').trim();
    const revRes = await axios.post(
      API_ENDPOINTS.adk.templateLanguageReview,
      {
        items: [
          {
            title: reviewTitle,
            baselineContent: standardBlock,
            generatedContent: useCptColor ? cptHtmlToPlainText(cleaned) : cleaned,
          },
        ],
        // Wire field name kept as-is (server contract); the review agent's
        // own prompt wording no longer assumes this is specifically intake
        // data — see TemplateLanguageReviewAgent.js.
        clinicalIntakeContext: usdmContext,
        templateId: 'default',
      },
      { timeout: 600000 }
    );
    const reviews = Array.isArray(revRes.data?.reviews) ? revRes.data.reviews : [];
    const r = reviews.find((x) => String(x?.title || '').trim() === reviewTitle) || reviews[0];
    const decision = String(r?.decision || '').toUpperCase().trim();
    const revised = String(r?.revised_plain_text || r?.revisedPlainText || '').trim();
    if (decision === 'REVISION_REQUIRED' && revised) {
      const revisedNoSiblingHeadings = stripSiblingHeadingLines(revised, fieldName);
      const revisedCleaned = useCptColor
        ? coerceCptTemplateHtml(revisedNoSiblingHeadings) || cleaned
        : normalizeTemplateLanguagePlainText(revisedNoSiblingHeadings);
      finalText = stripLeadingDuplicateHeading(revisedCleaned, meta.sectionLabel);
    }
  } catch (adkErr) {
    console.warn('generateUsdmTemplateField: ADK review failed, keeping LLM text:', adkErr);
  }

  // Grammar guard: capitalize sentence-starting words (and the "I" pronoun)
  // that came back lowercase from the model — see grammarGuards.js.
  finalText = applyGrammarGuardsToHtml(finalText);

  return { ok: true, finalText };
}
